import React from 'react';
import { 
  SkeletonHero, 
  SkeletonStats, 
  SkeletonCard,
  SkeletonTitle,
  SkeletonText,
  SkeletonParagraph,
  SkeletonButton,
  SkeletonTimelineCard,
  Skeleton
} from '../ui/skeletons';

// About Page Full Skeleton
const AboutPageSkeleton = () => (
  <div className="bg-[#FAF6F1] min-h-screen">
    {/* Header Skeleton */}
    <div className="fixed top-0 left-0 right-0 z-50 px-3 py-3">
      <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center justify-between">
        <Skeleton className="w-32 h-8 rounded-lg" />
        <div className="hidden lg:flex items-center gap-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="w-20 h-4 rounded" />
          ))}
        </div>
        <SkeletonButton size="md" />
      </div>
    </div>

    <main>
      {/* Hero Section Skeleton */}
      <section className="relative min-h-[80vh] overflow-hidden p-3">
        <SkeletonHero className="h-[calc(80vh-1.5rem)]" />
      </section>

      {/* Stats Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-white rounded-3xl overflow-hidden relative min-h-[400px]">
          <Skeleton className="absolute inset-0" />
          <div className="absolute inset-0 z-10 flex items-center justify-center py-16 px-8">
            <div className="max-w-[1000px] mx-auto w-full">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonStats key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-gradient-to-br from-[#D4E0D0] via-[#E0EAD8] to-[#D4E0D0] rounded-3xl py-12 px-6 lg:px-8">
          <div className="max-w-[1200px] mx-auto">
            {/* Title */}
            <div className="text-center mb-10">
              <SkeletonText width="80px" className="h-3 mx-auto mb-3" />
              <SkeletonTitle size="xl" className="mx-auto w-80" />
            </div>
            
            {/* Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Photo */}
              <div className="lg:col-span-4">
                <Skeleton className="w-full h-[500px] rounded-2xl" />
              </div>
              
              {/* Cards + Quote */}
              <div className="lg:col-span-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="bg-white/80 rounded-2xl p-6 space-y-4">
                      <Skeleton className="w-12 h-12 rounded-xl" />
                      <SkeletonTitle size="sm" className="w-24" />
                      <SkeletonParagraph lines={3} />
                    </div>
                  ))}
                </div>
                
                {/* Quote */}
                <div className="bg-[#1E1E1E] rounded-2xl p-8 space-y-4">
                  <Skeleton className="h-24 bg-white/10 rounded" />
                  <div className="flex items-center justify-center gap-4">
                    <Skeleton className="w-12 h-12 rounded-full bg-white/20" />
                    <div className="space-y-1">
                      <Skeleton className="w-24 h-4 bg-white/20 rounded" />
                      <Skeleton className="w-32 h-3 bg-white/10 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-gradient-to-br from-[#F5E6D3] via-[#FAF0E6] to-[#F5E6D3] rounded-3xl min-h-[600px] py-16 px-8">
          <div className="max-w-[1400px] mx-auto">
            {/* Title */}
            <div className="text-center mb-16">
              <SkeletonText width="100px" className="h-3 mx-auto mb-4" />
              <SkeletonTitle size="2xl" className="mx-auto w-96" />
            </div>
            
            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className={`bg-white rounded-3xl p-8 space-y-6 ${i === 1 ? 'lg:translate-y-8' : ''}`}>
                  <Skeleton className="w-16 h-16 rounded-2xl" />
                  <SkeletonTitle size="md" className="w-40" />
                  <SkeletonParagraph lines={3} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section Skeleton */}
      <section className="min-h-screen bg-[#FAF6F1] px-3 py-8">
        <div className="window-content bg-white rounded-3xl h-[calc(100vh-4rem)] relative overflow-hidden">
          {/* Header */}
          <div className="absolute top-8 left-0 right-0 text-center z-20">
            <SkeletonText width="100px" className="h-3 mx-auto mb-2" />
            <SkeletonTitle size="lg" className="mx-auto w-64" />
          </div>
          
          {/* Year Display */}
          <div className="absolute left-8 lg:left-16 top-1/2 -translate-y-1/2 z-30">
            <Skeleton className="w-32 h-24 lg:w-48 lg:h-36 rounded-xl" />
          </div>
          
          {/* Timeline Line */}
          <div className="absolute left-1/2 top-24 bottom-24 w-1 -translate-x-1/2 bg-gradient-to-b from-[#E8E4DF] via-[#E8E4DF] to-[#E8E4DF] rounded-full z-10" />
          
          {/* Card */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-full max-w-4xl mx-auto px-8 lg:px-16">
              <div className="absolute top-1/2 -translate-y-1/2 w-[45%] right-[52%]">
                <SkeletonTimelineCard />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section Skeleton */}
      <section className="py-4 px-3 pb-6">
        <div className="window-content bg-gradient-to-br from-[#FFDAB3] via-[#FFE4C9] to-[#FFDAB3] rounded-3xl min-h-[400px] py-16 px-8">
          <div className="max-w-[900px] mx-auto text-center">
            <SkeletonTitle size="xl" className="mx-auto w-96 mb-6" />
            <Skeleton className="h-6 w-80 mx-auto rounded mb-10" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SkeletonButton size="xl" className="w-48" />
              <SkeletonButton size="xl" className="w-48" />
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export { AboutPageSkeleton };
